import { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import {
    FileText,
    Printer,
    Download,
    Activity,
    Pill,
    AlertTriangle,
    User,
    Calendar,
    Sparkles,
    MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, differenceInYears } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Badge } from '@/components/ui/badge';
import { VitalSignsChart } from '@/components/dashboard/VitalSignsChart';
import { usePatientVitalRanges } from '@/hooks/usePatientVitalRanges';

interface PatientReportProps {
    patientId: string;
    isPrintView?: boolean;
}

export function PatientReport({ patientId, isPrintView = false }: PatientReportProps) {
    const { t } = useTranslation();
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const [reportSummary, setReportSummary] = useState<string | null>(null);
    const reportRef = useRef<HTMLDivElement>(null);
    const { getRangeForVitalType } = usePatientVitalRanges(patientId);

    // Fetch Patient Details
    const { data: patient, isLoading: isPatientLoading } = useQuery({
        queryKey: ['patient-report', patientId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('patients')
                .select('*')
                .eq('id', patientId)
                .single();
            if (error) throw error;
            return data;
        }
    });

    // Fetch Active Medications
    const { data: medications, isLoading: isMedsLoading } = useQuery({
        queryKey: ['patient-report-meds', patientId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('care_plan_tasks')
                .select('*')
                .eq('patient_id', patientId)
                .eq('is_active', true)
                .in('task_type', ['fixed_medication', 'sporadic_medication']);
            if (error) throw error;
            return data;
        }
    });

    // Fetch Recent Incidents (Last 30 days)
    const { data: incidents, isLoading: isIncidentsLoading } = useQuery({
        queryKey: ['patient-report-incidents', patientId],
        queryFn: async () => {
            const startDate = subDays(new Date(), 30).toISOString();
            const { data, error } = await supabase
                .from('task_executions')
                .select('*, care_plan_tasks(name, task_type)')
                .eq('patient_id', patientId)
                .gte('executed_at', startDate)
                .order('executed_at', { ascending: false });

            if (error) throw error;
            // Filter manually to be safe about joined table logic and avoid 400 errors
            return (data || []).filter(e =>
                e.adhoc_task_type === 'incident' ||
                (e.care_plan_tasks && (e.care_plan_tasks as any).task_type === 'incident')
            );
        }
    });

    // Fetch Physical Activities (Last 30 days)
    const { data: activities, isLoading: isActivitiesLoading } = useQuery({
        queryKey: ['patient-report-activities', patientId],
        queryFn: async () => {
            const startDate = subDays(new Date(), 30).toISOString();
            const { data, error } = await supabase
                .from('task_executions')
                .select('*, care_plan_tasks(name, task_type)')
                .eq('patient_id', patientId)
                .gte('executed_at', startDate)
                .order('executed_at', { ascending: false });

            if (error) throw error;
            // Filter manually for physical activities
            return (data || []).filter(e =>
                e.adhoc_task_type === 'physical_activity' ||
                (e.care_plan_tasks && (e.care_plan_tasks as any).task_type === 'physical_activity')
            );
        }
    });

    // Fetch Vitals (Last 30 days)
    const { data: vitals, isLoading: isVitalsLoading } = useQuery({
        queryKey: ['patient-report-vitals', patientId],
        queryFn: async () => {
            const startDate = subDays(new Date(), 30).toISOString();
            const { data, error } = await supabase
                .from('task_executions')
                .select('*, care_plan_tasks(name, vital_type)')
                .eq('patient_id', patientId)
                .gte('executed_at', startDate)
                .not('vital_value', 'is', null)
                .order('executed_at', { ascending: false });

            if (error) throw error;
            return data;
        }
    });

    const handleGenerateSummary = async () => {
        if (isGeneratingSummary || reportSummary) return; // Prevent duplicate calls
        setIsGeneratingSummary(true);
        try {
            const { data, error } = await supabase.functions.invoke('generate-patient-monitor', {
                body: { patientId }
            });

            if (error) throw error;
            setReportSummary(data.summary);
        } catch (error) {
            console.error('Error generating summary:', error);
            // Don't show toast on auto-generation, just log
        } finally {
            setIsGeneratingSummary(false);
        }
    };

    // Auto-generate summary when patient data is loaded
    useEffect(() => {
        if (patient && !reportSummary && !isGeneratingSummary) {
            handleGenerateSummary();
        }
    }, [patient]);

    const handlePrint = () => {
        window.print();
    };

    const handleSharePDF = async () => {
        if (!reportRef.current || !patient) return;

        setIsSharing(true);
        const originalWidth = reportRef.current.style.width;
        const originalMinWidth = reportRef.current.style.minWidth;

        try {
            // Force desktop-like width for consistent PDF layout and full-width charts
            reportRef.current.style.width = '1024px';
            reportRef.current.style.minWidth = '1024px';

            // Give time for Recharts ResponsiveContainer to re-render at new width
            await new Promise(resolve => setTimeout(resolve, 800));

            const canvas = await html2canvas(reportRef.current, {
                scale: 2, // Higher quality
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: 1024
            });

            // Restore original styles immediately after capture
            reportRef.current.style.width = originalWidth;
            reportRef.current.style.minWidth = originalMinWidth;

            const imgData = canvas.toDataURL('image/jpeg', 0.7);
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'px',
                format: [canvas.width / 2, canvas.height / 2],
                compress: true // Enable internal PDF compression
            });

            pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width / 2, canvas.height / 2, undefined, 'FAST');
            const pdfBlob = pdf.output('blob');
            const fileName = `Relatorio_Medico_${patient.nickname.replace(/\s+/g, '_')}_30_dias.pdf`;

            // NATIVE PLATFORM sharing (Android/iOS)
            if (Capacitor.isNativePlatform()) {
                const reader = new FileReader();
                reader.readAsDataURL(pdfBlob);
                reader.onloadend = async () => {
                    const base64data = reader.result as string;
                    // Extract base64 part
                    const base64Content = base64data.split(',')[1];

                    try {
                        // Write to cache directory (clean up previous file first)
                        await Filesystem.writeFile({
                            path: fileName,
                            data: base64Content,
                            directory: Directory.Cache,
                        });

                        // Share the file
                        await Share.share({
                            title: `Relatório Médico: ${patient.nickname}`,
                            text: `Relatório clínico dos últimos 30 dias de ${patient.nickname}.`,
                            files: [await Filesystem.getUri({ directory: Directory.Cache, path: fileName }).then(r => r.uri)],
                        });
                    } catch (shareErr) {
                        console.error('Share error:', shareErr);
                        toast.error('Erro ao compartilhar o arquivo.');
                    }
                };
            } else if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], fileName, { type: 'application/pdf' })] })) {
                // WEB SHARE API fallback (if supported and not native)
                const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
                await navigator.share({
                    files: [file],
                    title: `Relatório Médico: ${patient.nickname}`,
                    text: `Relatório clínico dos últimos 30 dias de ${patient.nickname}.`,
                });
            } else {
                // Desktop or Browser fallback: Download file
                const url = URL.createObjectURL(pdfBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                toast.success('PDF gerado e baixado com sucesso!');
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
            // Restore styles on error too
            if (reportRef.current) {
                reportRef.current.style.width = originalWidth;
                reportRef.current.style.minWidth = originalMinWidth;
            }
            toast.error('Erro ao gerar PDF. Tente novamente.');
        } finally {
            setIsSharing(false);
        }
    };

    const currentMeds = medications?.filter(m => !m.is_sporadic) || [];
    const sporadicMeds = medications?.filter(m => m.is_sporadic) || [];
    const isLoading = isPatientLoading || isMedsLoading || isIncidentsLoading || isActivitiesLoading || isVitalsLoading;

    if (isLoading) {
        return <div className="space-y-4 p-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
        </div>;
    }

    if (!patient) return null;

    return (
        <div className="space-y-8 p-6 print:p-0 max-w-5xl mx-auto bg-white print:bg-white min-h-screen">
            <Helmet>
                <title>Relatório Médico: {patient.nickname} - CareOS</title>
                <meta name="description" content={`Confira o relatório clínico de ${patient.nickname} dos últimos 30 dias.`} />
                {/* Open Graph for WhatsApp/Social Media */}
                <meta property="og:title" content={`Relatório Médico: ${patient.nickname}`} />
                <meta property="og:description" content={`Visão geral clínica dos últimos 30 dias - CareOS`} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
            </Helmet>

            {/* Header / Actions - Hidden on Print */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center print:hidden mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold">Relatório Médico</h2>
                    <p className="text-muted-foreground">Visão geral dos últimos 30 dias</p>
                </div>
                <div className="flex gap-2">
                    {Capacitor.isNativePlatform() ? (
                        <Button
                            variant="default"
                            onClick={handleSharePDF}
                            disabled={isSharing}
                            className="bg-success hover:bg-success/90 text-success-foreground"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            {isSharing ? 'Gerando PDF...' : 'Compartilhar PDF'}
                        </Button>
                    ) : (
                        <Button variant="outline" onClick={handlePrint} disabled={isSharing}>
                            <Printer className="w-4 h-4 mr-2" />
                            Imprimir
                        </Button>
                    )}
                </div>
            </div>

            {/* Report Content */}
            <div className="print:block space-y-8 bg-white p-4" id="report-content" ref={reportRef}>

                {/* Patient Header */}
                <div className="flex items-start gap-6 border-b pb-6">
                    <SignedAvatar
                        src={patient.photo_url}
                        bucket="patient-photos"
                        className="w-24 h-24 border-2 border-primary/10 rounded-full"
                        fallback={
                            <span className="text-2xl">{patient.nickname?.[0]}</span>
                        }
                    />
                    <div className="space-y-2 flex-1">
                        <h1 className="text-3xl font-bold text-primary">{patient.nickname}</h1>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-8 text-sm">
                            <div>
                                <span className="text-muted-foreground block">Idade</span>
                                <span className="font-medium">
                                    {patient.birth_date
                                        ? `${differenceInYears(new Date(), new Date(patient.birth_date + 'T00:00:00'))} anos`
                                        : 'N/A'}
                                </span>
                            </div>
                            <div className="col-span-2">
                                <span className="text-muted-foreground block">Detalhes de Saúde</span>
                                <span className="font-medium">{patient.health_details || 'Não informado'}</span>
                            </div>
                            <div className="col-span-3 mt-2">
                                <span className="text-muted-foreground block">Observações</span>
                                <p className="text-sm border-l-2 border-primary/20 pl-3 py-1 mt-1 bg-muted/30">
                                    {patient.notes || 'Sem observações adicionais.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* AI Summary Section */}
                <section className="space-y-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-sporadic" />
                        Resumo Clínico (IA)
                    </h3>

                     <Card className="border-sporadic/20 bg-sporadic/5 shadow-none">
                        <CardContent className="pt-6">
                            {isGeneratingSummary ? (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-sm text-sporadic">
                                        <Sparkles className="w-4 h-4 animate-pulse" />
                                        <span>Gerando resumo inteligente...</span>
                                    </div>
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-[90%]" />
                                    <Skeleton className="h-4 w-[80%]" />
                                    <Skeleton className="h-4 w-[85%]" />
                                </div>
                            ) : reportSummary ? (
                                <div className="prose prose-sm max-w-none text-foreground/80 whitespace-pre-wrap">
                                    {reportSummary}
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground italic">Não foi possível gerar o resumo.</p>
                            )}
                            {/* Fallback for print if no summary */}
                            {!reportSummary && !isGeneratingSummary && (
                                <p className="hidden print:block text-muted-foreground italic">Resumo não gerado no momento da impressão.</p>
                            )}
                        </CardContent>
                    </Card>
                </section>

                {/* Medications */}
                <section className="space-y-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2 border-b pb-2">
                        <Pill className="w-5 h-5" />
                        Medicamentos
                    </h3>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-medium text-sm text-muted-foreground mb-3 uppercase tracking-wider">Uso Contínuo</h4>
                            {currentMeds.length > 0 ? (
                                <ul className="space-y-2">
                                    {currentMeds.map(med => (
                                        <li key={med.id} className="flex justify-between items-start p-2 bg-muted/20 rounded-md">
                                            <div>
                                                <span className="font-medium block">{med.name}</span>
                                                <span className="text-xs text-muted-foreground">{med.dose || ''} {med.dose_unit || ''} • {med.schedule_times?.join(', ') || 'Sem horário'}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-muted-foreground italic">Nenhum medicamento de uso contínuo cadastrado.</p>
                            )}
                        </div>

                        <div>
                            <h4 className="font-medium text-sm text-muted-foreground mb-3 uppercase tracking-wider">Esporádicos (SOS)</h4>
                            {sporadicMeds.length > 0 ? (
                                <ul className="space-y-2">
                                    {sporadicMeds.map(med => (
                                        <li key={med.id} className="flex justify-between items-start p-2 bg-muted/20 rounded-md">
                                            <div>
                                                <span className="font-medium block">{med.name}</span>
                                                <span className="text-xs text-muted-foreground">{med.dose || ''} {med.dose_unit || ''}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-muted-foreground italic">Nenhum medicamento esporádico cadastrado.</p>
                            )}
                        </div>
                    </div>
                </section>

                {/* Incidents & Activities Grid */}
                <div className="grid md:grid-cols-2 gap-8">
                    <section className="space-y-4">
                        <h3 className="text-xl font-semibold flex items-center gap-2 border-b pb-2">
                            <AlertTriangle className="w-5 h-5" />
                            Incidentes Recentes (30 dias)
                        </h3>
                        {incidents && incidents.length > 0 ? (
                            <div className="space-y-3">
                                {incidents.slice(0, 5).map(inc => (
                                    <div key={inc.id} className="flex gap-3 items-start border-l-2 border-destructive/50 pl-3 py-1">
                                        <span className="text-xs font-mono text-muted-foreground whitespace-nowrap pt-0.5">
                                            {format(new Date(inc.executed_at), 'dd/MM HH:mm')}
                                        </span>
                                        <div>
                                            <span className="font-medium block text-sm">
                                                {inc.care_plan_tasks?.name || inc.adhoc_name || 'Incidente'}
                                            </span>
                                            {inc.notes && <p className="text-xs text-muted-foreground">{inc.notes}</p>}
                                        </div>
                                    </div>
                                ))}
                                <p className="text-xs text-muted-foreground text-right">Total: {incidents.length} incidentes</p>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground italic">Nenhum incidente registrado nos últimos 30 dias.</p>
                        )}
                    </section>

                    <section className="space-y-4">
                        <h3 className="text-xl font-semibold flex items-center gap-2 border-b pb-2">
                            <Activity className="w-5 h-5" />
                            Atividades Físicas (30 dias)
                        </h3>
                        {activities && activities.length > 0 ? (
                            <div className="space-y-3">
                                {activities.slice(0, 5).map(act => (
                                    <div key={act.id} className="flex gap-3 items-start border-l-2 border-success/50 pl-3 py-1">
                                        <span className="text-xs font-mono text-muted-foreground whitespace-nowrap pt-0.5">
                                            {format(new Date(act.executed_at), 'dd/MM HH:mm')}
                                        </span>
                                        <div>
                                            <span className="font-medium block text-sm">
                                                {act.care_plan_tasks?.name || act.adhoc_name || 'Atividade'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                <p className="text-xs text-muted-foreground text-right">Total: {activities.length} atividades</p>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground italic">Nenhuma atividade registrada nos últimos 30 dias.</p>
                        )}
                    </section>
                </div>

                {/* Vital Signs Charts */}
                <section className="space-y-4 break-inside-avoid">
                    <h3 className="text-xl font-semibold flex items-center gap-2 border-b pb-2">
                        <Activity className="w-5 h-5" />
                        Sinais Vitais (Gráficos)
                    </h3>

                    <div className="grid grid-cols-1 gap-6 print:block">
                        {vitals && vitals.length > 0 ? (
                            Object.entries(
                                vitals.reduce((acc: any, curr: any) => {
                                    const type = curr.care_plan_tasks?.vital_type || curr.adhoc_vital_type;
                                    if (!type) return acc;
                                    if (!acc[type]) {
                                        const range = getRangeForVitalType(type);
                                        acc[type] = {
                                            vitalType: type,
                                            readings: [],
                                            unit: range?.unit ?? curr.care_plan_tasks?.unit,
                                            minAlertValue: range?.min_value ?? null,
                                            maxAlertValue: range?.max_value ?? null,
                                        };
                                    }
                                    acc[type].readings.push({
                                        executed_at: curr.executed_at,
                                        vital_value: curr.vital_value,
                                        vital_value_secondary: curr.vital_value_secondary
                                    });
                                    return acc;
                                }, {})
                            ).map(([type, data]: [string, any]) => (
                                <div key={type} className="break-inside-avoid">
                                    <VitalSignsChart
                                        vitalType={data.vitalType}
                                        readings={data.readings.sort((a: any, b: any) => new Date(a.executed_at).getTime() - new Date(b.executed_at).getTime())}
                                        minAlertValue={data.minAlertValue}
                                        maxAlertValue={data.maxAlertValue}
                                        unit={data.unit}
                                    />
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-muted-foreground italic">Nenhum sinal vital registrado nos últimos 30 dias.</p>
                        )}
                    </div>
                </section>

                {/* Footer */}
                <div className="text-center text-xs text-muted-foreground border-t pt-8 mt-12 print:mt-auto">
                    <p>Relatório gerado em {format(new Date(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}</p>
                    <p>CareOS - Sistema de Gestão de Cuidados</p>
                </div>
            </div>
        </div>
    );
}
